import React, { useState, useEffect } from 'react';
import { BrainCircuit, Send, Check, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAISettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/admin-ai`, authHeaders());
        setSettings({ ...res.data, phones_input: (res.data.admin_phones || []).join(', ') });
      } catch (e) {
        console.error('Error loading admin AI settings:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/admin-ai`, {
        enabled: settings.enabled,
        admin_phones: settings.phones_input.split(',').map((p) => p.trim()).filter(Boolean),
        report_email: settings.report_email || '',
        monthly_report_enabled: settings.monthly_report_enabled,
        weekly_report_enabled: !!settings.weekly_report_enabled,
        comeback_message_template: settings.comeback_message_template || '',
        comeback_discount_enabled: !!settings.comeback_discount_enabled,
        comeback_discount_percent: Number(settings.comeback_discount_percent) || 10,
        comeback_discount_valid_days: Number(settings.comeback_discount_valid_days) || 14,
        team_alert_template_name: settings.team_alert_template_name || '',
        team_alert_template_lang: settings.team_alert_template_lang || 'de',
      }, authHeaders());
      toast.success('Admin-KI Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReport = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/settings/admin-ai/send-report-now`, {}, authHeaders());
      const d = res.data;
      if (d.skipped === 'disabled') {
        toast.info('Monats-Report ist deaktiviert.');
      } else {
        toast.success(`Report gesendet: ${d.whatsapp_sent} WhatsApp, E-Mail: ${d.email_sent ? 'ja' : 'nein'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleSendWeeklyReport = async () => {
    setSendingWeekly(true);
    try {
      const res = await axios.post(`${API}/settings/admin-ai/send-weekly-report-now`, {}, authHeaders());
      const d = res.data;
      if (d.skipped === 'disabled') {
        toast.info('Wochen-Report ist deaktiviert.');
      } else {
        toast.success(`Wochen-Report gesendet: ${d.whatsapp_sent} WhatsApp, E-Mail: ${d.email_sent ? 'ja' : 'nein'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingWeekly(false);
    }
  };

  const handleTest = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    setTestResponse('');
    try {
      const res = await axios.post(`${API}/settings/admin-ai/test`, { message: testMsg }, authHeaders());
      setTestResponse(res.data.response);
    } catch (e) {
      toast.error('Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return null;

  return (
    <Card className="border-indigo-200" data-testid="admin-ai-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BrainCircuit className="text-indigo-600" />
              Admin-KI (Business-Assistent)
            </CardTitle>
            <CardDescription>
              Beantwortet Business-Fragen per WhatsApp – nur für die hinterlegten Admin-Nummern.
              Beispiele: „Welche Kunden fehlen seit 30 Tagen?", „Wer hat offene Rechnungen?", „Umsatz diesen Monat?"
            </CardDescription>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
            data-testid="admin-ai-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Admin-Telefonnummern (Komma-getrennt)</Label>
            <Input
              data-testid="admin-ai-phones"
              value={settings.phones_input}
              onChange={(e) => setSettings({ ...settings, phones_input: e.target.value })}
              placeholder="+41791234567, +41797654321"
            />
          </div>
          <div className="space-y-2">
            <Label>Report-E-Mail</Label>
            <Input
              data-testid="admin-ai-email"
              type="email"
              value={settings.report_email || ''}
              onChange={(e) => setSettings({ ...settings, report_email: e.target.value })}
              placeholder="info@strongfit.ch"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100">
          <div>
            <p className="text-sm font-medium">📊 Monatlicher Auto-Report</p>
            <p className="text-xs text-gray-600">
              Am 1. des Monats um 08:00: neue Kunden, Trainings, Umsatz pro Studio,
              offene Rechnungen, Trainer-Auslastung – per WhatsApp & E-Mail.
            </p>
          </div>
          <Switch
            checked={!!settings.monthly_report_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, monthly_report_enabled: !!v })}
            data-testid="admin-ai-report-toggle"
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100">
          <div>
            <p className="text-sm font-medium">📈 Wöchentlicher Automatik-Erfolgsreport</p>
            <p className="text-xs text-gray-600">
              Jeden Sonntag um 18:00: Mahnungen & bezahlte Rechnungen (CHF), Comeback-Codes,
              Empfehlungen, Bewertungs-Anfragen, zurückgeholte Risiko-Kunden – per WhatsApp & E-Mail.
            </p>
          </div>
          <Switch
            checked={!!settings.weekly_report_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, weekly_report_enabled: !!v })}
            data-testid="admin-ai-weekly-report-toggle"
          />
        </div>

        <div className="space-y-2">
          <Label>Comeback-Nachricht (Aktion „Winback an inaktive Kunden")</Label>
          <Textarea
            data-testid="admin-ai-comeback-template"
            rows={4}
            value={settings.comeback_message_template || ''}
            onChange={(e) => setSettings({ ...settings, comeback_message_template: e.target.value })}
          />
          <p className="text-xs text-gray-500">
            Platzhalter: {'{first_name}'}, {'{studio_name}'}, {'{booking_link}'}, {'{discount_code}'}, {'{discount_percent}'}, {'{valid_until}'} — Spam-Schutz: max. 1 Nachricht pro Kunde alle 30 Tage, max. 50 pro Aktion, Bestätigung per „JA SENDEN" nötig.
          </p>
        </div>

        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">🎁 Rabattcode automatisch beilegen</p>
              <p className="text-xs text-gray-600">
                Jeder Kunde erhält einen persönlichen, 1x einlösbaren Code (zentrales Rabattcode-System, gilt für Punktepakete & Abos).
              </p>
            </div>
            <Switch
              checked={!!settings.comeback_discount_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, comeback_discount_enabled: !!v })}
              data-testid="admin-ai-discount-toggle"
            />
          </div>
          {settings.comeback_discount_enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Rabatt in %</Label>
                <Input
                  data-testid="admin-ai-discount-percent"
                  type="number" min="1" max="100"
                  value={settings.comeback_discount_percent ?? 10}
                  onChange={(e) => setSettings({ ...settings, comeback_discount_percent: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Gültig (Tage)</Label>
                <Input
                  data-testid="admin-ai-discount-days"
                  type="number" min="1"
                  value={settings.comeback_discount_valid_days ?? 14}
                  onChange={(e) => setSettings({ ...settings, comeback_discount_valid_days: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Team-Alarm WhatsApp-Vorlage (Versand außerhalb 24h-Fenster) */}
        <div className="border-t pt-4 space-y-2">
          <Label className="text-sm font-semibold">WhatsApp-Vorlage für Team-Alarme (kurzfristige Absagen)</Label>
          <p className="text-xs text-gray-500">
            WhatsApp erlaubt Freitext ans Team nur innerhalb von 24 h. Für Alarme außerhalb dieses
            Fensters wird eine von Meta <strong>genehmigte Vorlage</strong> genutzt. Erstelle in Meta
            eine Utility-Vorlage mit <strong>genau einem Body-Parameter</strong>, z.B. Text:
            <span className="block mt-1 font-mono bg-gray-100 p-2 rounded">🚨 Kurzfristige Absage-Anfrage: {'{{1}}'} — bitte im StrongFit-Dashboard mit Ja/Nein entscheiden.</span>
            Trage dann den genehmigten Vorlagen-Namen hier ein. Leer lassen = nur Glocke + E-Mail + Freitext.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-1">
              <Label className="text-xs">Vorlagen-Name (genau wie in Meta)</Label>
              <Input
                data-testid="admin-ai-team-template-name"
                value={settings.team_alert_template_name || ''}
                onChange={(e) => setSettings({ ...settings, team_alert_template_name: e.target.value })}
                placeholder="z.B. team_absage_alarm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sprache</Label>
              <Input
                data-testid="admin-ai-team-template-lang"
                value={settings.team_alert_template_lang || 'de'}
                onChange={(e) => setSettings({ ...settings, team_alert_template_lang: e.target.value })}
                placeholder="de"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="admin-ai-save-btn">
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
          <Button variant="outline" onClick={handleSendReport} disabled={sending} data-testid="admin-ai-send-report-btn">
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Sendet...' : 'Report jetzt senden'}
          </Button>
          <Button variant="outline" onClick={handleSendWeeklyReport} disabled={sendingWeekly} data-testid="admin-ai-send-weekly-btn">
            <Send className="h-4 w-4 mr-2" />
            {sendingWeekly ? 'Sendet...' : 'Wochen-Report jetzt senden'}
          </Button>
        </div>

        <div className="pt-3 border-t space-y-2">
          <Label className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-indigo-600" />
            Admin-KI testen
          </Label>
          <div className="flex gap-2">
            <Input
              data-testid="admin-ai-test-input"
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTest()}
              placeholder="z.B. Welche Kunden fehlen seit 30 Tagen?"
            />
            <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="admin-ai-test-btn">
              {testing ? '...' : 'Fragen'}
            </Button>
          </div>
          {testResponse && (
            <pre className="text-sm bg-gray-50 border rounded-lg p-3 whitespace-pre-wrap" data-testid="admin-ai-test-response">
              {testResponse}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
